import amplitude, { Callback } from 'amplitude-js';
import {
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  SpanName,
  toSpanAttributes,
} from 'core/common/observability/entities';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { CookiesService } from 'core/common/services/cookie';
import { FunnelSettingsServiceInterface } from 'core/common/services/funnel-settings/interfaces';
import { generateUUID } from 'core/common/utils/generateUUID';
import { isSuccessStatusCode } from 'core/common/utils/http';
import { EventProperties, EventValue, ExtendedEventValue, isExtendedEvent } from '../../entities';
import { AmplitudeService } from '../../interfaces';

export class AmplitudeClientService implements AmplitudeService {
  private userId: string;
  private funnelStage = 'appnebula';
  private instance: amplitude.AmplitudeClient;
  private readonly deviceIdCookieName = 'device_id';

  constructor(
    private readonly observabilitySystem: ObservabilitySystem,
    private readonly cookiesService: CookiesService,
    private readonly funnelSettingsService: FunnelSettingsServiceInterface,
    apiKey: string,
    apiEndpoint: string,
  ) {
    this.instance = amplitude.getInstance();
    this.instance.init(apiKey, undefined, {
      apiEndpoint,
      deviceIdFromUrlParam: true,
    });
    this.userId = '';

    this.setDeviceId();
  }

  private setDeviceId(): string {
    const deviceId = this.cookiesService.get(this.deviceIdCookieName);

    if (!deviceId) {
      const newDeviceId = generateUUID();

      this.cookiesService.set(this.deviceIdCookieName, newDeviceId, { path: '/' });
      this.instance.setDeviceId(newDeviceId);

      return newDeviceId;
    }

    this.instance.setDeviceId(deviceId);

    return deviceId;
  }

  logEvent(
    eventName: string,
    data?: Record<string, unknown>,
    callback?: Callback,
    errorCallback?: Callback,
  ) {
    const span = this.observabilitySystem.startSpan(SpanName.TRACK_ANALYTICS_AMPLITUDE, {
      attributes: {
        [ATTRS_ANALYTICS_FUNNEL_NAME]: this.funnelSettingsService.getFunnelName(),
        [ATTRS_ANALYTICS_FUNNEL_LOCALE]: this.funnelSettingsService.getFunnelLocale(),
        eventName,
        ...toSpanAttributes(data || {}),
      },
    });

    this.instance.logEvent(
      eventName,
      data,
      (code, response, details) => {
        if (isSuccessStatusCode(code)) {
          span.addEvent('Succeed', {
            code,
            response,
            reason: details && details.reason,
          });
          span.succeed();
        }

        if (callback) {
          callback(code, response, details);
        }
      },
      (code, body, details) => {
        span.addEvent('Failed', {
          code,
          body,
          reason: details && details.reason,
        });
        span.fail(body);

        if (errorCallback) {
          errorCallback(code, body, details);
        }
      },
    );
  }

  trackEvent(event: EventValue, properties: EventProperties = {}) {
    if (isExtendedEvent(event)) {
      this.trackExtendedEvent(event, properties);
      return;
    }

    this.logEvent(event, properties);
  }

  private trackExtendedEvent(event: ExtendedEventValue, properties: EventProperties = {}) {
    this.logEvent(event.name, { ...event.properties, ...properties });
  }

  setUserProperties(userData: Record<string, unknown>) {
    this.instance.setUserProperties(userData);
  }

  setFeatureFlagsAttributes(attributes: Record<string, unknown>) {
    return this.setUserProperties({
      feature_flags_attributes: attributes,
    });
  }

  funnelOpened(funnelName: string) {
    this.setUserProperties({
      quiz: funnelName,
    });

    this.logEvent('funnel_open_appnebula', {
      funnel: funnelName,
    });
  }

  flowOpened(flowName: string): void {
    this.setUserProperties({
      nfb_flow: flowName,
    });

    this.logEvent('flow_open_appnebula', {
      nfb_flow: flowName,
    });
  }

  trackUserLanguage(language: string): void {
    this.setUserProperties({
      language,
    });

    this.logEvent('user_language_set_appnebula', {
      language,
    });
  }

  launch() {
    if (!localStorage.getItem('launched')) {
      localStorage.setItem('launched', 'true');
      this.logEvent('launch_first_time', {
        funnel_stage: this.funnelStage,
      });
    } else if (!sessionStorage.getItem('launched')) {
      sessionStorage.setItem('launched', 'true');
      this.logEvent('launch', {
        funnel_stage: this.funnelStage,
      });
    }
  }

  getDeviceId(): string {
    return this.instance.options.deviceId || '';
  }

  getSessionId(): string {
    return String(this.instance.getSessionId());
  }

  setUserId(userId: string) {
    this.instance.setUserId(userId);
    this.userId = userId;
  }

  setUserEmail(email: string) {
    this.setUserProperties({ email });
  }

  setUserIdProperty(id: string) {
    this.setUserProperties({ appnebula_id: id });
  }

  setUserExperimentGroupName(experimentName: string, groupName: string) {
    this.setUserProperties({
      [experimentName]: groupName,
    });
  }

  policyPageOpened(pageName: string, contextName: string, userId: string) {
    this.logEvent('policies_page_open', {
      page_name: pageName,
      funnel_stage: this.funnelStage,
    });

    this.setUserProperties({
      user_id: userId,
      context: contextName,
    });
  }

  faqPageOpened(pageName: string, contextName: string, userId: string) {
    this.logEvent('faq_page_open', {
      page_name: pageName,
      funnel_stage: this.funnelStage,
    });

    this.setUserProperties({
      user_id: userId,
      context: contextName,
    });
  }

  contactUsPageOpened(pageName: string, contextName: string, userId: string) {
    this.logEvent('support_form_open', {
      page_name: pageName,
      funnel_stage: this.funnelStage,
    });

    this.setUserProperties({
      user_id: userId,
      context: contextName,
    });
  }

  paymentCardClick() {
    this.logEvent('card_button_click', {
      funnel_stage: this.funnelStage,
    });
  }

  googlePayClick() {
    this.logEvent('googlepay_button_click', {
      funnel_stage: this.funnelStage,
    });
  }

  applePayClick() {
    this.logEvent('applepay_button_click', {
      funnel_stage: this.funnelStage,
    });
  }

  paypalPayClick() {
    this.logEvent('paypal_button_click', {
      funnel_stage: this.funnelStage,
    });
  }

  paymentCardChosen(userId: string) {
    this.setUserProperties({
      user_id: userId,
    });
    this.logEvent('payment_card_chosen', {
      funnel_stage: this.funnelStage,
    });
  }

  appleBtnChosen(userId: string) {
    this.setUserProperties({
      user_id: userId,
    });
    this.logEvent('payment_apple_pay_chosen', {
      funnel_stage: this.funnelStage,
    });
  }

  googleBtnChosen(userId: string) {
    this.setUserProperties({
      user_id: userId,
    });
    this.logEvent('payment_googlepay_chosen', {
      funnel_stage: this.funnelStage,
    });
  }

  paymentPaypalChosen(userId: string) {
    this.setUserProperties({
      user_id: userId,
    });
    this.logEvent('payment_paypal_chosen', {
      funnel_stage: this.funnelStage,
    });
  }

  paymentError(userId: string, reason: string) {
    this.setUserProperties({
      user_id: userId,
    });
    this.logEvent('payment_error', {
      reason,
      funnel_stage: this.funnelStage,
    });
  }

  paymentRetried(userId: string) {
    this.setUserProperties({
      user_id: userId,
    });
    this.logEvent('payment_retry', {
      funnel_stage: this.funnelStage,
    });
  }

  paymentSuccess({ productId, paymentMethod }: { productId: string; paymentMethod: string }) {
    this.setUserProperties({
      product_id: productId,
      payment_method: paymentMethod,
    });
    this.logEvent('payment_success', {
      product_id: productId,
      funnel_stage: this.funnelStage,
    });
  }

  emailUpsellScreenOpen(context: string) {
    this.logEvent('lp_email_upsell_screen_open', {
      context,
    });
  }

  purchaseSucceed(productId: string) {
    this.logEvent('payment_success', {
      product_id: productId,
      funnel_stage: this.funnelStage,
    });
  }

  purchaseError(productId: string) {
    this.logEvent('payment_error', {
      product_id: productId,
      funnel_stage: this.funnelStage,
    });
  }

  setUserAge(age: number) {
    this.setUserProperties({
      user_age: age,
    });
  }
}
